import type { ReactNode } from "react";

import type { ParametersGeoBoundingBox } from "@/lib/api";
import {
  BoundsPreviewMap,
  type BoundsPreviewInteractionMode,
} from "@/features/widget-forms/forms/weather-model-map/components/bounds-preview-map";
import Button from "@/components/button/button";
import Icon from "@/components/icon/icon";
import dialogStyles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";

export type MapWidgetEditorPreviewColumnStrings = {
  previewTitle: string;
  previewButton: string;
};

type MapWidgetEditorPreviewColumnProps = {
  bounds: ParametersGeoBoundingBox;
  baseLayerStyle: string;
  onBoundsChange: (bounds: ParametersGeoBoundingBox) => void;
  boundsInteractionMode: BoundsPreviewInteractionMode;
  fitBoundsPadding: number;
  onExpand: () => void;
  strings: MapWidgetEditorPreviewColumnStrings;
  /** Optional layer rendered on top of the map canvas (e.g. prototype marker preview). */
  mapOverlay?: ReactNode;
  /**
   * Full preview content that replaces the editable bounds map (e.g. the live
   * widget preview). When set, the bounds map + {@link mapOverlay} are not shown
   * and the content fills the pane below the header.
   */
  content?: ReactNode;
  /**
   * Optional controls rendered in the preview title bar, next to the expand
   * button (e.g. a widget-context toolbar). Must be mounted within the same
   * runtime context as {@link content} so its controls drive the preview.
   */
  toolbar?: ReactNode;
};

/**
 * Sidebar map preview for map widget CMS. Drag / overlay behaviour is controlled via
 * {@link boundsInteractionMode}; composers do not wire drag handles directly.
 */
export function MapWidgetEditorPreviewColumn({
  bounds,
  baseLayerStyle,
  onBoundsChange,
  boundsInteractionMode,
  fitBoundsPadding,
  onExpand,
  strings,
  mapOverlay,
  content,
  toolbar,
}: MapWidgetEditorPreviewColumnProps) {
  const showOverlay =
    boundsInteractionMode === "readOnly" || boundsInteractionMode === "editable";

  return (
    <div className={dialogStyles.previewWrapper}>
      <div className={dialogStyles.previewHeader}>
        <h3 className={dialogStyles.previewTitle}>{strings.previewTitle}</h3>
        <div className={dialogStyles.previewHeaderActions}>
          {toolbar}
          <Button
            type="button"
            variant="ghost"
            className={dialogStyles.expandButton}
            onClick={onExpand}
          >
            <Icon name="maximize-2" size={16} />
          </Button>
        </div>
      </div>
      <div className={dialogStyles.previewContent}>
        {content ? (
          <div className={dialogStyles.previewLiveHost}>{content}</div>
        ) : (
          <div className={dialogStyles.previewPlaceholder}>
            <div className={dialogStyles.previewMapHost}>
              <BoundsPreviewMap
                bounds={bounds}
                showBoundsOverlay={showOverlay}
                fitBoundsPadding={fitBoundsPadding}
                baseLayerStyle={baseLayerStyle}
                onBoundsChange={
                  boundsInteractionMode === "editable" ? onBoundsChange : undefined
                }
                boundsInteractionMode={boundsInteractionMode}
              />
              {mapOverlay}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
